import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useRecipes } from '@/contexts/RecipesContext';
import { Recipe, Ingredient } from '@/types';
import { X, Plus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ProtectedRoute from '@/components/ProtectedRoute';
import NutriscoreBar from '@/components/NutriscoreBar';
import { triggerFavoriteAdd } from '@/utils/onboardingEvents';

const RecipeForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { addRecipe, updateRecipe, getRecipe } = useRecipes();

  const emptyRecipe: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'> = {
    title: '',
    description: '',
    imageUrl: 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
    prepTime: 0,
    cookTime: 0,
    servings: 1,
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0,
    iron: 0,
    calcium: 0,
    zinc: 0,
    magnesium: 0,
    omega3: 0,
    nutriscore: 50,
    ingredients: [],
    instructions: [''],
    tags: [],
  };

  const [formData, setFormData] = useState<Omit<Recipe, 'id' | 'createdAt' | 'updatedAt'>>(emptyRecipe);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      console.log("Attempting to load recipe with ID:", id);
      
      // Remove any 'editar/' prefix from the ID
      const cleanId = id.replace(/^editar\//, '');
      console.log("Cleaned ID for recipe lookup:", cleanId);
      
      const recipe = getRecipe(cleanId);
      if (recipe) {
        console.log("Recipe found for editing:", recipe);
        const { id: recipeId, createdAt, updatedAt, ...recipeData } = recipe;
        if (!recipeData.nutriscore) {
          recipeData.nutriscore = 50;
        }
        setFormData(recipeData);
      } else {
        console.error("Recipe not found with ID:", cleanId);
        navigate('/admin/recetas');
        toast.error('Receta no encontrada');
      }
    }
  }, [id, isEditing, getRecipe, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: Number(value) || 0 });
  };

  const handleNutriscoreChange = (value: number) => {
    setFormData({ ...formData, nutriscore: value });
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [
        ...formData.ingredients,
        { id: `ing-${Date.now()}`, name: '', amount: 0, unit: '' },
      ],
    });
  };

  const updateIngredient = (index: number, field: keyof Ingredient, value: string | number) => {
    const updatedIngredients = [...formData.ingredients];
    
    if (field === 'amount') {
      updatedIngredients[index] = { 
        ...updatedIngredients[index], 
        [field]: Number(value) || 0 
      };
    } else {
      updatedIngredients[index] = { 
        ...updatedIngredients[index], 
        [field]: value 
      };
    }
    
    setFormData({ ...formData, ingredients: updatedIngredients });
  };

  const removeIngredient = (index: number) => {
    const updatedIngredients = [...formData.ingredients];
    updatedIngredients.splice(index, 1);
    setFormData({ ...formData, ingredients: updatedIngredients });
  };

  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, ''],
    });
  };

  const updateInstruction = (index: number, value: string) => {
    const updatedInstructions = [...formData.instructions];
    updatedInstructions[index] = value;
    setFormData({ ...formData, instructions: updatedInstructions });
  };

  const removeInstruction = (index: number) => {
    const updatedInstructions = [...formData.instructions];
    updatedInstructions.splice(index, 1);
    setFormData({ ...formData, instructions: updatedInstructions });
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((t) => t !== tag),
    });
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.title) throw new Error('El título es obligatorio');
      if (formData.ingredients.length === 0) throw new Error('Añade al menos un ingrediente');
      if (formData.instructions.every(i => !i.trim())) throw new Error('Añade al menos una instrucción');

      const hasEmptyIngredient = formData.ingredients.some(i => !i.name.trim());
      const hasEmptyInstruction = formData.instructions.some(i => !i.trim());
      
      if (hasEmptyIngredient) throw new Error('Todos los ingredientes deben tener nombre');
      if (hasEmptyInstruction) throw new Error('Todas las instrucciones deben tener contenido');

      const formDataWithDefaults = {
        ...formData,
        iron: formData.iron || 0,
        calcium: formData.calcium || 0,
        zinc: formData.zinc || 0,
        magnesium: formData.magnesium || 0,
        omega3: formData.omega3 || 0
      };

      if (isEditing && id) {
        // Clean ID by removing any 'editar/' prefix
        const cleanId = id.replace(/^editar\//, '');
        await updateRecipe(cleanId, formDataWithDefaults);
        toast.success('Receta actualizada correctamente');
      } else {
        await addRecipe(formDataWithDefaults);
        toast.success('Receta creada correctamente');
      }
      
      // Trigger the favorite-added event when a recipe is saved
      triggerFavoriteAdd();
      
      navigate('/admin/recetas');
    } catch (error) {
      toast.error(`Error: ${(error as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ProtectedRoute requireAdmin={true}>
      <div className="flex flex-col min-h-screen">
        <Navbar />

        <main className="flex-grow py-12 px-4 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Button 
              variant="ghost" 
              className="mb-6 flex items-center gap-2 text-vegana-primary hover:bg-transparent hover:text-vegana-accent"
              onClick={() => navigate('/admin/recetas')}
            >
              <ArrowLeft size={16} />
              Volver a Recetas
            </Button>

            <h1 className="text-3xl font-bold text-vegana-primary mb-8">
              {isEditing ? 'Editar Receta' : 'Nueva Receta'}
            </h1>

            <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm space-y-8">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold border-b pb-2">Información Básica</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Título*</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descripción*</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="imageUrl">URL de Imagen</Label>
                  <Input
                    id="imageUrl"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="prepTime">Tiempo de Preparación (min)</Label>
                    <Input
                      id="prepTime"
                      name="prepTime"
                      type="number"
                      min="0"
                      value={formData.prepTime}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cookTime">Tiempo de Cocción (min)</Label>
                    <Input
                      id="cookTime"
                      name="cookTime"
                      type="number"
                      min="0"
                      value={formData.cookTime}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="servings">Porciones</Label>
                    <Input
                      id="servings"
                      name="servings"
                      type="number"
                      min="1"
                      value={formData.servings}
                      onChange={handleNumericChange}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold border-b pb-2">Información Nutricional (por porción)</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="calories">Calorías (kcal)</Label>
                    <Input
                      id="calories"
                      name="calories"
                      type="number"
                      min="0"
                      value={formData.calories}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="protein">Proteínas (g)</Label>
                    <Input
                      id="protein"
                      name="protein"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.protein}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="carbs">Carbohidratos (g)</Label>
                    <Input
                      id="carbs"
                      name="carbs"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.carbs}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fat">Grasas (g)</Label>
                    <Input
                      id="fat"
                      name="fat"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.fat}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="iron">Hierro (mg)</Label>
                    <Input
                      id="iron"
                      name="iron"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.iron || 0}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="calcium">Calcio (mg)</Label>
                    <Input
                      id="calcium"
                      name="calcium"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.calcium || 0}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zinc">Zinc (mg)</Label>
                    <Input
                      id="zinc"
                      name="zinc"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.zinc || 0}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="magnesium">Magnesio (mg)</Label>
                    <Input
                      id="magnesium"
                      name="magnesium"
                      type="number"
                      min="0"
                      step="0.1"
                      value={formData.magnesium || 0}
                      onChange={handleNumericChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="omega3">Omega 3 (g)</Label>
                    <Input
                      id="omega3"
                      name="omega3"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.omega3 || 0}
                      onChange={handleNumericChange}
                    />
                  </div>
                </div>
                
                <div className="space-y-2 pt-4">
                  <Label htmlFor="nutriscore">Nutriscore (1-100)</Label>
                  <NutriscoreBar 
                    value={formData.nutriscore} 
                    onChange={handleNutriscoreChange} 
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <h2 className="text-xl font-semibold">Ingredientes*</h2>
                  <Button 
                    type="button" 
                    variant="outline"
                    size="sm"
                    onClick={addIngredient}
                    className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary/10"
                  >
                    <Plus size={16} className="mr-2" />
                    Añadir Ingrediente
                  </Button>
                </div>

                {formData.ingredients.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">No hay ingredientes añadidos</p>
                ) : (
                  <div className="space-y-2">
                    {formData.ingredients.map((ingredient, index) => (
                      <div key={ingredient.id} className="flex items-center gap-2">
                        <Input
                          placeholder="Cantidad"
                          type="number"
                          min="0"
                          step="0.1"
                          value={ingredient.amount}
                          onChange={(e) => updateIngredient(index, 'amount', e.target.value)}
                          className="w-20"
                        />
                        <Input
                          placeholder="Unidad"
                          value={ingredient.unit}
                          onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                          className="w-24"
                        />
                        <Input
                          placeholder="Ingrediente"
                          value={ingredient.name}
                          onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                          className="flex-grow"
                        />
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeIngredient(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center border-b pb-2">
                  <h2 className="text-xl font-semibold">Instrucciones*</h2>
                  <Button 
                    type="button" 
                    variant="outline"
                    size="sm"
                    onClick={addInstruction}
                    className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary/10"
                  >
                    <Plus size={16} className="mr-2" />
                    Añadir Paso
                  </Button>
                </div>

                <div className="space-y-3">
                  {formData.instructions.map((instruction, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="min-w-7 h-7 flex items-center justify-center bg-vegana-primary text-white rounded-full">
                        {index + 1}
                      </div>
                      <Textarea
                        placeholder="Paso de la receta..."
                        value={instruction}
                        onChange={(e) => updateInstruction(index, e.target.value)}
                        className="flex-grow"
                        rows={2}
                      />
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeInstruction(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold border-b pb-2">Etiquetas</h2>

                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Añadir etiqueta"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                    />
                    <Button 
                      type="button"
                      onClick={addTag}
                      className="bg-vegana-primary hover:bg-vegana-primary/90 whitespace-nowrap"
                    >
                      Añadir
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag) => (
                      <div 
                        key={tag} 
                        className="flex items-center gap-1 bg-vegana-secondary/20 text-vegana-primary px-3 py-1 rounded-full"
                      >
                        <span>{tag}</span>
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-vegana-primary hover:text-vegana-accent"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="bg-vegana-primary hover:bg-vegana-primary/90 w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? isEditing 
                      ? 'Actualizando...' 
                      : 'Guardando...'
                    : isEditing 
                      ? 'Actualizar Receta' 
                      : 'Guardar Receta'
                  }
                </Button>
              </div>
            </form>
          </div>
        </main>

        <Footer />
      </div>
    </ProtectedRoute>
  );
};

export default RecipeForm;
